/**
 * word-roots.ts — Static Arabic word root dictionary for inline word lookup.
 *
 * Each entry is keyed by the Arabic word as it typically appears in the Quran
 * (surface form, no diacritics stripped).  Where one root has many surface
 * forms, each form is listed separately so that the lookup in WordPopup is a
 * simple O(1) map access rather than a root-extraction step.
 *
 * Columns:
 *   root        — trilateral (or quad) Arabic root, e.g. "ك-ت-ب"
 *   meaning     — concise English gloss of the root
 *   transliteration — simplified Latin transliteration of the surface word
 *   notes       — optional grammatical note (verb form, noun pattern, etc.)
 */

export interface WordRootEntry {
  root: string;
  meaning: string;
  transliteration: string;
  notes?: string;
}

const WORD_ROOTS: Record<string, WordRootEntry> = {
  // ─── ب-س-م ────────────────────────────────────────────────────────────────
  'بِسْمِ': { root: 'س-م-و', meaning: 'name', transliteration: 'bismi', notes: 'Prefixed preposition بِ + اسم' },
  'اللَّهِ': { root: 'إ-ل-ه', meaning: 'God / the Deity', transliteration: 'allāhi' },
  'الرَّحْمَٰنِ': { root: 'ر-ح-م', meaning: 'Most Gracious / Abundantly Merciful', transliteration: 'al-raḥmāni', notes: 'فَعْلَان hyperbolic pattern' },
  'الرَّحِيمِ': { root: 'ر-ح-م', meaning: 'Most Merciful / Especially Merciful', transliteration: 'al-raḥīmi', notes: 'فَعِيل pattern' },

  // ─── Common opening words ──────────────────────────────────────────────────
  'الْحَمْدُ': { root: 'ح-م-د', meaning: 'praise / gratitude', transliteration: 'al-ḥamdu' },
  'لِلَّهِ': { root: 'إ-ل-ه', meaning: 'for/to God', transliteration: 'lillāhi', notes: 'Preposition لِ + اللَّه' },
  'رَبِّ': { root: 'ر-ب-ب', meaning: 'Lord / Sustainer / Nurturer', transliteration: 'rabbi' },
  'الْعَالَمِينَ': { root: 'ع-ل-م', meaning: 'worlds / all creation', transliteration: 'al-ʿālamīna', notes: 'Sound masculine plural' },
  'مَالِكِ': { root: 'م-ل-ك', meaning: 'owner / king / master', transliteration: 'māliki' },
  'يَوْمِ': { root: 'ي-و-م', meaning: 'day', transliteration: 'yawmi' },
  'الدِّينِ': { root: 'د-ي-ن', meaning: 'religion / judgement / debt', transliteration: 'al-dīni' },
  'إِيَّاكَ': { root: '', meaning: 'You alone (emphatic pronoun)', transliteration: 'iyyāka', notes: 'Pronoun of specialisation' },
  'نَعْبُدُ': { root: 'ع-ب-د', meaning: 'we worship', transliteration: 'naʿbudu', notes: 'Mudāriʿ (present) 1st person plural' },
  'وَإِيَّاكَ': { root: '', meaning: 'and You alone', transliteration: 'wa-iyyāka' },
  'نَسْتَعِينُ': { root: 'ع-و-ن', meaning: 'we seek help', transliteration: 'nastaʿīnu', notes: 'Form X استفعال' },
  'اهْدِنَا': { root: 'ه-د-ي', meaning: 'guide us', transliteration: 'ihdinā', notes: 'Imperative Form I' },
  'الصِّرَاطَ': { root: 'ص-ر-ط', meaning: 'path / straight way', transliteration: 'al-ṣirāṭa' },
  'الْمُسْتَقِيمَ': { root: 'ق-و-م', meaning: 'straight / upright', transliteration: 'al-mustaqīma', notes: 'Form X active participle' },

  // ─── ق-و-ل ────────────────────────────────────────────────────────────────
  'قَالَ': { root: 'ق-و-ل', meaning: 'he said', transliteration: 'qāla', notes: 'Perfect Form I' },
  'قُلْ': { root: 'ق-و-ل', meaning: 'say! (imperative)', transliteration: 'qul', notes: 'Imperative Form I, hollow verb' },
  'يَقُولُ': { root: 'ق-و-ل', meaning: 'he says', transliteration: 'yaqūlu', notes: 'Imperfect Form I' },

  // ─── ع-ل-م ────────────────────────────────────────────────────────────────
  'عَلِمَ': { root: 'ع-ل-م', meaning: 'he knew', transliteration: 'ʿalima' },
  'يَعْلَمُ': { root: 'ع-ل-م', meaning: 'he knows', transliteration: 'yaʿlamu' },
  'عِلْمٌ': { root: 'ع-ل-م', meaning: 'knowledge', transliteration: 'ʿilmun' },
  'عَالِمٌ': { root: 'ع-ل-م', meaning: 'one who knows / scholar', transliteration: 'ʿālimun', notes: 'Active participle Form I' },

  // ─── ك-ت-ب ────────────────────────────────────────────────────────────────
  'كَتَبَ': { root: 'ك-ت-ب', meaning: 'he wrote / prescribed', transliteration: 'kataba' },
  'كِتَابٌ': { root: 'ك-ت-ب', meaning: 'book / scripture', transliteration: 'kitābun', notes: 'فِعَال noun pattern' },
  'الْكِتَابِ': { root: 'ك-ت-ب', meaning: 'the Book / Scripture', transliteration: 'al-kitābi' },
  'مَكْتُوبٌ': { root: 'ك-ت-ب', meaning: 'written / prescribed', transliteration: 'maktūbun', notes: 'Passive participle Form I' },

  // ─── ن-ز-ل ────────────────────────────────────────────────────────────────
  'نَزَّلَ': { root: 'ن-ز-ل', meaning: 'sent down (gradually)', transliteration: 'nazzala', notes: 'Form II (tafʿīl) — intensive/gradual' },
  'أَنْزَلَ': { root: 'ن-ز-ل', meaning: 'sent down', transliteration: 'anzala', notes: 'Form IV (ifʿāl)' },
  'نَزَلَ': { root: 'ن-ز-ل', meaning: 'descended', transliteration: 'nazala', notes: 'Form I' },
  'تَنْزِيلٌ': { root: 'ن-ز-ل', meaning: 'revelation / gradual sending down', transliteration: 'tanzīlun', notes: 'Verbal noun Form II' },

  // ─── ر-ح-م ────────────────────────────────────────────────────────────────
  'رَحِمَ': { root: 'ر-ح-م', meaning: 'showed mercy', transliteration: 'raḥima' },
  'رَحْمَةٌ': { root: 'ر-ح-م', meaning: 'mercy / compassion', transliteration: 'raḥmatun' },
  'رَحِيمٌ': { root: 'ر-ح-م', meaning: 'especially merciful', transliteration: 'raḥīmun', notes: 'فَعِيل intensive adjective' },

  // ─── آ-م-ن ────────────────────────────────────────────────────────────────
  'آمَنَ': { root: 'أ-م-ن', meaning: 'believed / had faith', transliteration: 'āmana', notes: 'Form IV hamzated' },
  'مُؤْمِنٌ': { root: 'أ-م-ن', meaning: 'believer', transliteration: 'muʾminun', notes: 'Active participle Form IV' },
  'الْمُؤْمِنِينَ': { root: 'أ-م-ن', meaning: 'the believers', transliteration: 'al-muʾminīna', notes: 'Sound masculine plural' },
  'إِيمَانٌ': { root: 'أ-م-ن', meaning: 'faith / belief', transliteration: 'īmānun', notes: 'Verbal noun Form IV' },

  // ─── ص-ل-و ────────────────────────────────────────────────────────────────
  'صَلَاةٌ': { root: 'ص-ل-و', meaning: 'prayer / salah', transliteration: 'ṣalātun' },
  'الصَّلَاةَ': { root: 'ص-ل-و', meaning: 'the prayer', transliteration: 'al-ṣalāta' },
  'صَلَّى': { root: 'ص-ل-و', meaning: 'prayed / blessed', transliteration: 'ṣallā', notes: 'Form II (tafʿīl)' },

  // ─── ز-ك-و ────────────────────────────────────────────────────────────────
  'زَكَاةٌ': { root: 'ز-ك-و', meaning: 'zakat / purifying charity', transliteration: 'zakātun' },
  'الزَّكَاةَ': { root: 'ز-ك-و', meaning: 'the zakat', transliteration: 'al-zakāta' },

  // ─── ص-و-م ────────────────────────────────────────────────────────────────
  'صَوْمٌ': { root: 'ص-و-م', meaning: 'fasting', transliteration: 'ṣawmun' },
  'صِيَامٌ': { root: 'ص-و-م', meaning: 'fasting (extended)', transliteration: 'ṣiyāmun', notes: 'Variant verbal noun' },

  // ─── ح-ج-ج ────────────────────────────────────────────────────────────────
  'حَجٌّ': { root: 'ح-ج-ج', meaning: 'pilgrimage (Hajj)', transliteration: 'ḥajjun' },
  'الْحَجِّ': { root: 'ح-ج-ج', meaning: 'the Hajj', transliteration: 'al-ḥajji' },

  // ─── ج-ن-ن ────────────────────────────────────────────────────────────────
  'جَنَّةٌ': { root: 'ج-ن-ن', meaning: 'garden / paradise', transliteration: 'jannatun' },
  'الْجَنَّةَ': { root: 'ج-ن-ن', meaning: 'Paradise', transliteration: 'al-jannata' },

  // ─── ن-ا-ر / ن-و-ر ───────────────────────────────────────────────────────────
  'نَارٌ': { root: 'ن-و-ر', meaning: 'fire / hellfire', transliteration: 'nārun', notes: 'Root ن-و-ر (same family as nūr/light; fire illuminates)' },
  'النَّارِ': { root: 'ن-و-ر', meaning: 'the Fire', transliteration: 'al-nāri', notes: 'Root ن-و-ر (same family as nūr/light)' },

  // ─── ن-و-ر ────────────────────────────────────────────────────────────────
  'نُورٌ': { root: 'ن-و-ر', meaning: 'light', transliteration: 'nūrun' },
  'النُّورِ': { root: 'ن-و-ر', meaning: 'the Light', transliteration: 'al-nūri' },

  // ─── ق-ر-أ ────────────────────────────────────────────────────────────────
  'اقْرَأْ': { root: 'ق-ر-أ', meaning: 'read! / recite!', transliteration: 'iqraʾ', notes: 'Imperative Form I, hamzated' },
  'قُرْآنٌ': { root: 'ق-ر-أ', meaning: 'Quran / that which is recited', transliteration: 'qurʾānun', notes: 'فُعْلَان verbal noun' },
  'الْقُرْآنَ': { root: 'ق-ر-أ', meaning: 'the Quran', transliteration: 'al-qurʾāna' },

  // ─── ه-د-ي ────────────────────────────────────────────────────────────────
  'هَدَى': { root: 'ه-د-ي', meaning: 'guided', transliteration: 'hadā' },
  'هُدًى': { root: 'ه-د-ي', meaning: 'guidance', transliteration: 'hudan' },
  'الْهُدَى': { root: 'ه-د-ي', meaning: 'the guidance', transliteration: 'al-hudā' },

  // ─── ت-ق-و ────────────────────────────────────────────────────────────────
  'تَقْوَى': { root: 'و-ق-ي', meaning: 'God-consciousness / piety / fear of God', transliteration: 'taqwā' },
  'الْمُتَّقِينَ': { root: 'و-ق-ي', meaning: 'the God-fearing / the pious', transliteration: 'al-muttaqīna', notes: 'Active participle Form VIII (iftaʿala)' },

  // ─── س-ب-ح ────────────────────────────────────────────────────────────────
  'سُبْحَانَ': { root: 'س-ب-ح', meaning: 'glory be to / exalted is', transliteration: 'subḥāna', notes: 'Verbal noun used as exclamation' },
  'يُسَبِّحُ': { root: 'س-ب-ح', meaning: 'glorifies / exalts', transliteration: 'yusabbiḥu', notes: 'Form II imperfect' },

  // ─── ش-ك-ر ────────────────────────────────────────────────────────────────
  'شَكَرَ': { root: 'ش-ك-ر', meaning: 'was grateful', transliteration: 'shakara' },
  'شُكْرٌ': { root: 'ش-ك-ر', meaning: 'gratitude / thankfulness', transliteration: 'shukrun' },
  'شَاكِرٌ': { root: 'ش-ك-ر', meaning: 'grateful / thankful', transliteration: 'shākirun', notes: 'Active participle Form I' },

  // ─── ع-ب-د ────────────────────────────────────────────────────────────────
  'عَبَدَ': { root: 'ع-ب-د', meaning: 'worshipped', transliteration: 'ʿabada' },
  'عِبَادَةٌ': { root: 'ع-ب-د', meaning: 'worship', transliteration: 'ʿibādatun' },
  'عَبْدٌ': { root: 'ع-ب-د', meaning: 'servant / slave', transliteration: 'ʿabdun' },
  'عِبَادُ': { root: 'ع-ب-د', meaning: 'servants (of God)', transliteration: 'ʿibādu' },

  // ─── خ-ل-ق ────────────────────────────────────────────────────────────────
  'خَلَقَ': { root: 'خ-ل-ق', meaning: 'created', transliteration: 'khalaqa' },
  'الْخَالِقُ': { root: 'خ-ل-ق', meaning: 'the Creator', transliteration: 'al-khāliqu', notes: 'Active participle Form I; one of the 99 Names' },
  'خَلِيفَةٌ': { root: 'خ-ل-ف', meaning: 'vicegerent / successor / caliph', transliteration: 'khalīfatun', notes: 'فَعِيلَة pattern' },
};

/**
 * Look up an Arabic word (surface form) and return its root entry,
 * or `null` if not found.
 */
export function lookupWord(word: string): WordRootEntry | null {
  // Try exact match first
  const exact = WORD_ROOTS[word];
  if (exact) return exact;

  // Try stripping common diacritics (tashkīl) for a looser match
  const stripped = word.replace(/[\u0610-\u061A\u064B-\u065F]/g, '');
  for (const [key, entry] of Object.entries(WORD_ROOTS)) {
    const keyStripped = key.replace(/[\u0610-\u061A\u064B-\u065F]/g, '');
    if (keyStripped === stripped) return entry;
  }

  return null;
}
